import { useState, useCallback, useMemo } from 'react';
import { toast } from '@lb-classroom/ui';
import {
  useCreateCertificationMutation,
  useUpdateCertificationMutation,
} from '@root/hooks/queries';
import CertificationItemValidator from '@root/lib/validators/CertificationItemValidator';
import type {
  EditCertificationItemModalContainerProps,
  ValidationResult,
  CertificationTypeOption,
} from './edit-certification-item-modal.types';

const DEFAULT_VALIDATION: ValidationResult = { success: true, message: '' };

const CERTIFICATION_TYPE_OPTIONS: CertificationTypeOption[] = [
  { value: 'TESOL', id: '0', content: 'TESOL' },
  { value: 'TEFL', id: '1', content: 'TEFL' },
  {
    value: 'LET',
    id: '2',
    content: 'LET - Licensure Examination for Teachers',
  },
  { value: 'Other', id: '4', content: 'Other Certification' },
];

export const useEditCertificationItemModalService = (
  props: EditCertificationItemModalContainerProps,
) => {
  const { hideModal, isNew = false, certification } = props;

  const [hasChanges, setHasChanges] = useState(false);

  // Mutations
  const createCertificationMutation = useCreateCertificationMutation();
  const updateCertificationMutation = useUpdateCertificationMutation();
  const isSubmitting = createCertificationMutation.isPending || updateCertificationMutation.isPending;

  // Form fields
  const [type, setType] = useState(certification.type || '');
  const [customTitle, setCustomTitle] = useState(
    certification.custom_title || '',
  );
  const [institution, setInstitution] = useState(certification.institution || '');
  const [yearFrom, setYearFrom] = useState<number | null>(
    certification.year_from,
  );
  const [yearTo, setYearTo] = useState<number | null>(certification.year_to);

  // Validation states
  const [typeValidation, setTypeValidation] =
    useState<ValidationResult>(DEFAULT_VALIDATION);
  const [customTitleValidation, setCustomTitleValidation] =
    useState<ValidationResult>(DEFAULT_VALIDATION);
  const [yearRangeValidation, setYearRangeValidation] =
    useState<ValidationResult>(DEFAULT_VALIDATION);

  // Year options
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: Array<{ name: string; value: number | null }> = [];
    years.push({ name: 'Select Year', value: null });
    for (let year = 1960; year <= currentYear + 4; year++) {
      years.push({ name: year.toString(), value: year });
    }
    return years;
  }, []);

  const handleFieldChange = useCallback(
    <T>(setter: React.Dispatch<React.SetStateAction<T>>) =>
      (value: T) => {
        setter(value);
        setHasChanges(true);
      },
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const data = {
        type,
        customTitle,
        yearFrom,
        yearTo,
      };

      const validator = new CertificationItemValidator(data);
      const result = (await validator.validate()) as {
        valid: boolean;
        errors?: Array<{ type: string; message: string }>;
      };

      if (!result.valid) {
        const errors = result.errors || [];

        const typeError = errors.find(
          (e: { type: string }) => e.type === 'Certification Title',
        );
        const customTitleError = errors.find(
          (e: { type: string }) => e.type === 'Custom Title',
        );
        const yearRangeError = errors.find(
          (e: { type: string }) => e.type === 'Duration',
        );

        setTypeValidation(
          typeError
            ? { success: false, message: typeError.message }
            : DEFAULT_VALIDATION,
        );
        setCustomTitleValidation(
          customTitleError
            ? { success: false, message: customTitleError.message }
            : DEFAULT_VALIDATION,
        );
        setYearRangeValidation(
          yearRangeError
            ? { success: false, message: yearRangeError.message }
            : DEFAULT_VALIDATION,
        );
        return;
      }

      // Clear validation errors
      setTypeValidation(DEFAULT_VALIDATION);
      setCustomTitleValidation(DEFAULT_VALIDATION);
      setYearRangeValidation(DEFAULT_VALIDATION);

      const params = {
        type,
        institution,
        custom_title: customTitle,
        year_from: yearFrom,
        year_to: yearTo,
      };

      const mutationOptions = {
        onSuccess: () => {
          toast.success(
            isNew
              ? 'Successfully created the certification.'
              : 'Successfully updated the certification.'
          );
          hideModal();
        },
        onError: (error: Error) => {
          const errorMessage = error.message || 'Failed to save certification';
          toast.error(errorMessage);
        },
      };

      if (isNew) {
        createCertificationMutation.mutate(params, mutationOptions);
      } else {
        updateCertificationMutation.mutate({ id: String(certification.id), ...params }, mutationOptions);
      }
    },
    [type, customTitle, institution, yearFrom, yearTo, certification.id, isNew, hideModal, createCertificationMutation, updateCertificationMutation],
  );

  return {
    isNew,
    isSubmitting,
    hasChanges,
    type,
    customTitle,
    institution,
    yearFrom,
    yearTo,
    typeOptions: CERTIFICATION_TYPE_OPTIONS,
    yearOptions,
    typeValidation,
    customTitleValidation,
    yearRangeValidation,
    message: props.message,
    messageType: props.messageType,
    onTypeChange: handleFieldChange(setType),
    onCustomTitleChange: handleFieldChange(setCustomTitle),
    onInstitutionChange: handleFieldChange(setInstitution),
    onYearFromChange: handleFieldChange(setYearFrom),
    onYearToChange: handleFieldChange(setYearTo),
    onSubmit: handleSubmit,
    onClose: hideModal,
  };
};
